"use client";

import { formatMoney } from "@/modules/eventRegistration/features/EventRegistration/utils/formatMoney";
import { stageMembershipLabel } from "@/modules/eventRegistration/features/EventRegistration/utils/stageMembershipLabel";

interface PriceSummaryProps {
  /**
   * Per-seat price, ALREADY coverage-adjusted by the API
   * (GetEventSessionRegistrationInfoResponseDTO sends the snapshot's
   * seatPrice, which is 0 when stage/TA coverage applies) — so the line
   * items and Total here are correct as plain multiplication.
   */
  price: number;
  attendeeCount: number;
  waitlistedCount?: number;
  isCoveredByTotalAccess?: boolean;
  isCoveredByStage?: boolean;
  /** The company's effective stage name, for the stage-coverage note. */
  stageName?: string | null;
  /**
   * Whether the logged-in user's own auto-added seat is in the count.
   * Undefined when no seat was offered (already enrolled) — nothing shown.
   */
  includesSelf?: boolean;
}

export function PriceSummary({
  price,
  attendeeCount,
  waitlistedCount = 0,
  isCoveredByTotalAccess = false,
  isCoveredByStage = false,
  stageName = null,
  includesSelf,
}: PriceSummaryProps): React.ReactElement {
  const confirmedCount = attendeeCount - waitlistedCount;

  return (
    <div className="space-y-2 text-sm">
      {isCoveredByStage ? (
        <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 mb-1">
          <p className="text-xs text-emerald-700 dark:text-emerald-300">
            Included with your {stageMembershipLabel(stageName)} — no charge for
            this event.
          </p>
        </div>
      ) : isCoveredByTotalAccess ? (
        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2 mb-1">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            Included with your Total Access subscription — no charge for this
            event.
          </p>
        </div>
      ) : null}
      <div className="flex justify-between">
        <span className="text-muted-foreground">
          {confirmedCount} confirmed &times; ${formatMoney(price)}
          {includesSelf === true && " · includes your seat"}
          {includesSelf === false && " · you're not attending"}
        </span>
        <span className="font-medium">
          ${formatMoney(price * confirmedCount)}
        </span>
      </div>
      {waitlistedCount > 0 && (
        <div className="flex justify-between text-muted-foreground">
          <span>{waitlistedCount} waitlisted</span>
          <span>$0.00</span>
        </div>
      )}
      <div className="border-t border-border pt-2 flex justify-between">
        <span className="font-semibold">Total</span>
        <span className="font-semibold text-base tabular-nums">
          ${formatMoney(price * confirmedCount)}
        </span>
      </div>
    </div>
  );
}
